import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint, getEndpointWithId } from '../../apiConstants';
import {
  RoleData,
  RoleApiResponse,
  PermissionData,
  PermissionApiResponse,
  RolePermissionData,
  CreateRolePayload,
  UpdateRolePayload,
  RoleObject,
} from './roles.types';

// Standardized GET function for roles list
export async function fetchRoles({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  name,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  name?: string;
  searched?: boolean;
}): Promise<RoleData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };
  if (name) params.roleName = name; // Changed from 'name' to 'roleName'

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ROLES.LIST);
  const response: RoleApiResponse[] = await api.get<RoleApiResponse[]>(endpoint, params);
  
  return response.map((role: RoleApiResponse) => ({
    id: role.id,
    name: role.roleName, // Use roleName instead of name
  }));
}

// Standardized GET function for roles count
export async function fetchRolesCount({
  clientCode,
  name,
  searched = true,
}: {
  clientCode: string;
  name?: string;
  searched?: boolean;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };
  if (name) params.roleName = name; // Changed from 'name' to 'roleName'

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ROLES.COUNT);
  
  try {
    const response = await api.get(endpoint, params);
    
    // Handle different response formats
    if (typeof response === 'number') {
      return response;
    } else if (typeof response === 'string') {
      // Handle response like "1 8" - extract the count
      const parts = response.trim().split(' ');
      if (parts.length > 0) {
        const countValue = parseInt(parts[parts.length - 1], 10); // Take the last number
        return isNaN(countValue) ? 0 : countValue;
      }
      return 0;
    } else if (response && typeof response === 'object' && 'count' in response) {
      const countValue = (response as any).count;
      return typeof countValue === 'number' ? countValue : 0;
    } else {
      logger.warn('Unexpected count response format:', response);
      return 0;
    }
  } catch (error) {
    logger.error('Error fetching roles count:', error);
    return 0;
  }
}

// GET role by ID - returns full role object for update
export async function fetchRoleById({
  clientCode,
  roleId,
}: {
  clientCode: string;
  roleId: number;
}): Promise<RoleObject> {
  const endpoint = getEndpointWithId(clientCode, API_ENDPOINTS.ROLES.ROLE_BY_ID, roleId);
  const response: any = await api.get(endpoint);
  const role = response?.object ?? response;
  if (role?.roleName != null || role?.id != null) return { ...role, id: role.id ?? roleId };
  throw new Error('Invalid role response');
}

// CREATE role - POST { roleName }, Response: { success, object, messages }
export async function createRole({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: CreateRolePayload;
}): Promise<RoleObject> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ROLES.CREATE);
  const response: any = await api.post(endpoint, { roleName: payload.roleName });
  if (response?.success === false) {
    const msg = response?.messages?.join(', ') || 'Failed to create role';
    throw new Error(msg);
  }
  return response?.object ?? response;
}

// UPDATE role - PUT full role object, Response: { success, object, messages }
export async function updateRole({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: UpdateRolePayload;
}): Promise<RoleObject> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ROLES.UPDATE);
  const response: any = await api.put(endpoint, payload);
  if (response?.success === false) {
    const msg = response?.messages?.join(', ') || 'Failed to update role';
    throw new Error(msg);
  }
  return response?.object ?? response;
}

// GET permissions
export async function fetchPermissions({
  clientCode,
  firstResult = -1,
  maxResult = 0,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
}): Promise<PermissionData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
  };

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ROLES.PERMISSIONS);
  const response: PermissionApiResponse[] = await api.get<PermissionApiResponse[]>(endpoint, params);
  
  return response.map((permission: PermissionApiResponse) => ({
    id: permission.id,
    permissionName: permission.permissionName,
  }));
}

// GET role permissions
export async function fetchRolePermissions({
  clientCode,
  roleId,
  firstResult = -1,
  maxResult = 0,
}: {
  clientCode: string;
  roleId: number;
  firstResult?: number;
  maxResult?: number;
}): Promise<RolePermissionData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    roleId,
  };

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ROLES.ROLE_PERMISSIONS);
  const response: any[] = await api.get<any[]>(endpoint, params);

  return response.map((row: any) => {
    // Handle array format: [rolePermissionId, permissionId, roleId, permissionName] or [permissionId, permissionName, ...]
    if (Array.isArray(row)) {
      const permissionId = row[1] ?? row[0];
      const permissionName = typeof row[3] === 'string' ? row[3] : (row[2] ?? '');
      return { id: permissionId, permissionName };
    }
    // Handle object format: { permissionId, permissionName } or { id, permissionName }
    const permissionId = row.permissionId ?? row.id;
    const permissionName = row.permissionName ?? '';
    return { id: permissionId, permissionName };
  });
}

// POST save role permissions
// Payload: { roleId, list, orgId }, Response: { success, object, messages }
export async function saveRolePermissions({
  clientCode,
  roleId,
  permissionIds,
  orgId = '1',
}: {
  clientCode: string;
  roleId: number;
  permissionIds: number[];
  orgId?: string;
}): Promise<{ success: boolean; object: unknown; messages: string[] }> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.ROLES.SAVE_ROLE_PERMISSIONS);
  const response: any = await api.post(endpoint, {
    roleId: String(roleId),
    list: permissionIds,
    orgId: String(orgId),
  });
  if (response?.success === false) {
    const msg = response?.messages?.join(', ') || 'Failed to save role permissions';
    throw new Error(msg);
  }
  return response;
}
